import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { buildEditorSchema } from './email-editor-schema';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  finalize,
  take,
  takeUntil,
} from 'rxjs';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  EmailTemplateApiService,
  EmailTemplateRow,
} from 'src/app/core/services/email-template-api.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import {
  mergeEmailTemplateBody,
  splitEmailTemplateForEdit,
} from './email-template-body.util';
import {
  compileEmailSubjectPreview,
  parseMacroKeys,
  renderEmailPreviewInIframe,
} from './email-template-preview.util';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { PermissionService } from 'src/app/core/services/permission.service';

@Component({
  selector: 'app-edit-email-template',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule,
    NgxEditorModule,
    HasPermissionPipe,
  ],
  templateUrl: './edit-email-template.component.html',
  styleUrl: './edit-email-template.component.css',
})
export class EditEmailTemplateComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('previewFrame') previewFrame?: ElementRef<HTMLIFrameElement>;

  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(EmailTemplateApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly fb = inject(FormBuilder);
  private readonly permissionService = inject(PermissionService);
  private readonly destroy$ = new Subject<void>();

  editor!: Editor;
  readonly toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  templateId = 0;
  templateData: EmailTemplateRow | null = null;
  macroKeys: string[] = [];
  editorDragActive = false;
  loading = false;
  saving = false;
  sendingTest = false;
  loadError = '';
  saveError = '';
  testMailError = '';
  previewSubject = '';
  private templateParts = { prefix: '', body: '', suffix: '' };
  private previewFrameReady = false;
  private pendingPreviewRefresh = false;

  get canEditTemplate(): boolean {
    return this.permissionService.hasPermission(this.P.EMAIL_TEMPLATE_UPDATE);
  }

  form = this.fb.group({
    is_active: [true, Validators.required],
    subject: ['', Validators.required],
    html_content: ['', Validators.required],
    template_name: [''],
  });

  ngOnInit(): void {
    this.editor = new Editor({ schema: buildEditorSchema() });
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isFinite(id) || id < 1) {
      this.backToList();
      return;
    }
    this.templateId = id;
    this.loadTemplate();
    this.bindPreviewUpdates();
  }

  ngAfterViewInit(): void {
    this.previewFrameReady = true;
    if (this.pendingPreviewRefresh) {
      this.refreshPreview();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.editor?.destroy();
  }

  loadTemplate(): void {
    this.loadError = '';
    this.loading = true;
    this.api
      .getTemplateById(this.templateId)
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (env) => {
          const data = env.data;
          if (!data) {
            this.loadError = 'Invalid template data received.';
            return;
          }
          this.templateData = data;
          this.macroKeys = parseMacroKeys(data.macro);
          this.templateParts = splitEmailTemplateForEdit(data.html_content ?? '');
          const editableBody = this.templateParts.body;
          this.form.patchValue({
            is_active: data.is_active ?? true,
            subject: data.subject ?? '',
            html_content: editableBody,
            template_name: data.template_name ?? '',
          });
          queueMicrotask(() => {
            this.form.controls.html_content.setValue(editableBody, { emitEvent: false });
            if (!this.canEditTemplate) {
              this.form.disable({ emitEvent: false });
            }
            this.refreshPreview();
          });
        },
        error: (err) => {
          this.loadError =
            resolveApiErrorMessage(err) || 'Failed to load template data.';
        },
      });
  }

  private bindPreviewUpdates(): void {
    this.form.controls.html_content.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.refreshPreview());

    this.form.controls.subject.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.refreshPreviewSubject());
  }

  private refreshPreviewSubject(): void {
    this.previewSubject = compileEmailSubjectPreview(
      this.form.controls.subject.value ?? '',
      this.macroKeys,
    );
  }

  private refreshPreview(): void {
    this.refreshPreviewSubject();
    if (!this.previewFrameReady) {
      this.pendingPreviewRefresh = true;
      return;
    }
    this.pendingPreviewRefresh = false;

    const merged = mergeEmailTemplateBody(
      {
        ...this.templateParts,
        body: this.form.controls.html_content.value ?? '',
      },
      this.form.controls.template_name.value ?? this.templateData?.template_name ?? 'Email',
    );

    renderEmailPreviewInIframe(
      this.previewFrame?.nativeElement,
      merged,
      this.macroKeys,
    );
  }

  toggleStatusInForm(): void {
    this.form.patchValue({
      is_active: !this.form.controls.is_active.value,
    });
  }

  onKeywordDragStart(event: DragEvent, key: string): void {
    const token = `{{${key}}}`;
    event.dataTransfer?.setData('text/plain', token);
    event.dataTransfer?.setData('application/x-email-macro', key);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copy';
    }
  }

  onEditorDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.editorDragActive = true;
  }

  onEditorDragLeave(event: DragEvent): void {
    const related = event.relatedTarget as Node | null;
    const current = event.currentTarget as HTMLElement | null;
    if (current && related && current.contains(related)) {
      return;
    }
    this.editorDragActive = false;
  }

  onEditorDrop(event: DragEvent): void {
    event.preventDefault();
    this.editorDragActive = false;
    const plainToken = event.dataTransfer?.getData('text/plain') ?? '';
    const macroKey =
      event.dataTransfer?.getData('application/x-email-macro') ?? '';
    const token =
      plainToken === ''
        ? this.formatMacroTokenForDrop(macroKey)
        : plainToken;
    if (token) {
      this.insertMacroIntoEditor(token);
    }
  }

  private formatMacroTokenForDrop(key: string): string {
    if (!key) {
      return '';
    }
    return `{{${key}}}`;
  }

  insertMacroByKey(key: string): void {
    this.insertMacroIntoEditor(`{{${key}}}`);
  }

  private insertMacroIntoEditor(token: string): void {
    this.editor.commands.focus().insertText(token).exec();
  }

  sendTestMail(): void {
    this.testMailError = '';
    this.sendingTest = true;
    this.api
      .sendTestMail(this.templateId)
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        finalize(() => (this.sendingTest = false)),
      )
      .subscribe({
        next: (env) => {
          this.snackBar.open(
            env.message || 'Test email sent to your account.',
            'Dismiss',
            { duration: 5000 },
          );
        },
        error: (err) => {
          this.testMailError =
            resolveApiErrorMessage(err) || 'Could not send test email.';
        },
      });
  }

  submit(): void {
    this.saveError = '';
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const html_content = mergeEmailTemplateBody(
      {
        ...this.templateParts,
        body: value.html_content?.trim() ?? '',
      },
      value.template_name?.trim() ?? this.templateData?.template_name ?? 'Email',
    );

    this.saving = true;
    this.api
      .updateTemplate(this.templateId, {
        template_name: value.template_name?.trim() ?? '',
        subject: value.subject?.trim() ?? '',
        html_content,
        is_active: value.is_active ?? true,
      })
      .pipe(
        assertSuccessEnvelope(),
        take(1),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (env) => {
          this.snackBar.open(
            env.message || 'Email template updated successfully.',
            'Dismiss',
            { duration: 5000 },
          );
          this.backToList();
        },
        error: (err) => {
          this.saveError =
            resolveApiErrorMessage(err) || 'Could not update email template.';
        },
      });
  }

  backToList(): void {
    void this.router.navigate(['/system-preferences'], {
      queryParams: { section: 'tenant-email' },
    });
  }
}
